import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { ScrollReveal } from "@/components/ScrollReveal";
import {
  ArrowRight, BarChart3, Brain, ChevronRight, Globe, Landmark,
  LineChart, Lock, Mail, MessageSquare, Rocket, Search, Shield,
  Sparkles, Star, Target, TrendingUp, Users, Zap, Check, Play,
  Building2, ShoppingBag, ChefHat, Briefcase, Eye, Send, Loader2, UserPlus,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import LandingChat from "@/components/landing/LandingChat";

/* ─── Animated counter ─── */
const Counter = ({ end, suffix = "", prefix = "" }: { end: number; suffix?: string; prefix?: string }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let start = 0;
          const duration = 1800;
          const step = (ts: number) => {
            if (!start) start = ts;
            const progress = Math.min((ts - start) / duration, 1);
            setCount(Math.floor(progress * end));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

/* ─── Typing effect for hero ─── */
const TypingText = ({ words }: { words: string[] }) => {
  const [idx, setIdx] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const word = words[idx];
    const timeout = deleting ? 40 : 80;
    if (!deleting && displayed === word) {
      setTimeout(() => setDeleting(true), 2200);
      return;
    }
    if (deleting && displayed === "") {
      setDeleting(false);
      setIdx((i) => (i + 1) % words.length);
      return;
    }
    const timer = setTimeout(() => {
      setDisplayed(deleting ? word.slice(0, displayed.length - 1) : word.slice(0, displayed.length + 1));
    }, timeout);
    return () => clearTimeout(timer);
  }, [displayed, deleting, idx, words]);

  return (
    <span className="text-gradient">
      {displayed}
      <span className="inline-block w-[3px] h-[0.85em] bg-primary ml-1 animate-pulse align-middle" />
    </span>
  );
};

/* ─── Dashboard mockup component ─── */
const DashboardMockup = () => (
  <div className="relative w-full h-[420px]">
    {/* ═══ BACK DEVICE: Main dashboard screen ═══ */}
    <div
      className="absolute top-0 left-0 w-[420px] rounded-[16px] border-[3px] border-[hsl(220,10%,25%)] bg-[hsl(222,28%,8%)] overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      style={{ transform: "perspective(1400px) rotateY(-12deg) rotateX(5deg)", transformOrigin: "center center" }}
    >
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <div className="h-5 w-5 rounded bg-primary flex items-center justify-center">
              <span className="text-[6px] font-bold text-primary-foreground">G</span>
            </div>
            <span className="text-[7px] font-bold text-foreground">Grafton IA</span>
            <span className="text-[5px] bg-primary/20 text-primary px-1 py-0.5 rounded-full">PRO</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-3 px-2 rounded bg-secondary/40 flex items-center"><span className="text-[5px] text-muted-foreground">Bonjour, Marc</span></div>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="w-[60px] shrink-0 space-y-1">
            {["Dashboard", "Briefing", "Ventes", "Trésorerie", "Emails", "Agents IA"].map((item, i) => (
              <div key={item} className={`rounded px-1.5 py-1 text-[5px] ${i === 0 ? "bg-primary/15 text-primary font-bold" : "text-muted-foreground/60"}`}>
                {item}
              </div>
            ))}
          </div>
          <div className="flex-1 space-y-2">
            <div className="grid grid-cols-4 gap-1">
              {[
                { label: "Clients actifs", value: "247", color: "text-foreground" },
                { label: "Prospects", value: "89", color: "text-foreground" },
                { label: "Conversion", value: "12.4%", color: "text-emerald-400" },
                { label: "CA mensuel", value: "58.1k€", color: "text-emerald-400" },
              ].map((k) => (
                <div key={k.label} className="rounded bg-secondary/20 border border-border/15 p-1.5">
                  <p className="text-[4px] text-muted-foreground/60">{k.label}</p>
                  <p className={`text-[8px] font-bold ${k.color}`}>{k.value}</p>
                </div>
              ))}
            </div>
            <div className="rounded bg-secondary/10 border border-border/15 p-2">
              <p className="text-[5px] text-muted-foreground/60 mb-1">Performance commerciale 2026</p>
              <div className="h-16 flex items-end gap-[2px]">
                <svg className="w-full h-full" viewBox="0 0 200 64" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="hg1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(38 90% 55%)" stopOpacity="0.5" />
                      <stop offset="100%" stopColor="hsl(38 90% 55%)" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="hg2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(150 60% 45%)" stopOpacity="0.3" />
                      <stop offset="100%" stopColor="hsl(150 60% 45%)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,50 Q25,42 50,36 Q75,28 100,24 Q125,20 150,14 Q175,10 200,6 L200,64 L0,64Z" fill="url(#hg1)" />
                  <path d="M0,50 Q25,42 50,36 Q75,28 100,24 Q125,20 150,14 Q175,10 200,6" fill="none" stroke="hsl(38 90% 55%)" strokeWidth="1.5" />
                  <path d="M0,54 Q25,48 50,52 Q75,44 100,40 Q125,38 150,32 Q175,28 200,22 L200,64 L0,64Z" fill="url(#hg2)" />
                  <path d="M0,54 Q25,48 50,52 Q75,44 100,40 Q125,38 150,32 Q175,28 200,22" fill="none" stroke="hsl(150 60% 45%)" strokeWidth="1" strokeDasharray="3 2" />
                </svg>
              </div>
            </div>
            <div className="rounded bg-primary/[0.06] border border-primary/15 p-1.5 flex items-start gap-1">
              <Sparkles className="h-2.5 w-2.5 text-primary shrink-0 mt-0.5" />
              <p className="text-[5px] text-muted-foreground leading-relaxed">
                <span className="text-primary font-bold">Grafton IA</span> : 3 opportunités détectées. Relance recommandée avant vendredi.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* ═══ FRONT DEVICE 1: iPhone — Grafton Agents ═══ */}
    <div
      className="absolute bottom-0 left-[200px] w-[150px] z-20 rounded-[20px] border-[3px] border-[hsl(220,10%,20%)] bg-[hsl(222,28%,8%)] overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.5)]"
      style={{ transform: "perspective(1200px) rotateY(-6deg) rotateX(2deg)" }}
    >
      <div className="h-4 bg-[hsl(222,28%,6%)] flex items-center justify-center">
        <div className="h-1.5 w-8 rounded-full bg-background/20" />
      </div>
      <div className="p-2 space-y-1.5">
        <div className="flex items-center gap-1">
          <div className="h-4 w-4 rounded-md bg-primary/20 flex items-center justify-center">
            <Brain className="h-2.5 w-2.5 text-primary" />
          </div>
          <span className="text-[6px] font-bold">Grafton Agents</span>
        </div>
        {[
          { name: "Prospection", status: "Actif", color: "bg-emerald-500" },
          { name: "Qualification", status: "Actif", color: "bg-emerald-500" },
          { name: "Closing", status: "En cours", color: "bg-amber-500" },
        ].map((a) => (
          <div key={a.name} className="rounded-lg bg-secondary/20 border border-border/15 p-1.5 flex items-center gap-1.5">
            <div className={`h-1.5 w-1.5 rounded-full ${a.color}`} />
            <span className="text-[5px] font-medium flex-1">{a.name}</span>
            <span className={`text-[4px] ${a.color === "bg-emerald-500" ? "text-emerald-400" : "text-amber-400"}`}>{a.status}</span>
          </div>
        ))}
        <div className="rounded-lg bg-primary/[0.06] border border-primary/15 p-1.5">
          <p className="text-[4px] text-primary font-bold mb-0.5">Rapport du jour</p>
          <p className="text-[4px] text-muted-foreground">3 prospects qualifiés, 2 relances envoyées automatiquement.</p>
        </div>
      </div>
    </div>

    {/* ═══ FRONT DEVICE 2: iPhone — Tunnel de vente ═══ */}
    <div
      className="absolute bottom-4 right-0 w-[140px] z-10 rounded-[20px] border-[3px] border-[hsl(220,10%,20%)] bg-[hsl(222,28%,8%)] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.45)]"
      style={{ transform: "perspective(1200px) rotateY(6deg) rotateX(3deg)" }}
    >
      <div className="h-4 bg-[hsl(222,28%,6%)] flex items-center justify-center">
        <div className="h-1.5 w-8 rounded-full bg-background/20" />
      </div>
      <div className="p-2 space-y-1.5">
        <div className="flex items-center gap-1">
          <div className="h-4 w-4 rounded-md bg-violet-500/20 flex items-center justify-center">
            <Target className="h-2.5 w-2.5 text-violet-500" />
          </div>
          <span className="text-[6px] font-bold">Tunnel de vente</span>
        </div>
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/15 p-2 text-center">
          <p className="text-[4px] text-muted-foreground">Pipeline actif</p>
          <p className="text-[10px] font-bold text-emerald-400">12 deals</p>
          <p className="text-[4px] text-emerald-400/60">78 500 € en négociation</p>
        </div>
        {[
          { label: "Campagne LinkedIn B2B", stage: "En cours", color: "text-emerald-400" },
          { label: "Séquence Email PME", stage: "Brouillon", color: "text-amber-400" },
        ].map((tx) => (
          <div key={tx.label} className="flex items-center justify-between rounded bg-secondary/15 px-1.5 py-1">
            <span className="text-[5px]">{tx.label}</span>
            <span className={`text-[4px] font-bold ${tx.color}`}>{tx.stage}</span>
          </div>
        ))}
      </div>
    </div>

    <div className="absolute -inset-12 bg-primary/[0.025] blur-[60px] rounded-full -z-10" />
  </div>
);

/* ─── Data ─── */

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
}

/* ─── Public Chat Section ─── */
const MAX_FREE_EXCHANGES = 3;

const CHAT_PLACEHOLDERS = [
  "Décris ton projet ou pose ta question...",
  "Ask me anything about your business...",
  "Quel est ton plus gros défi aujourd'hui ?",
  "What's your biggest challenge right now?",
  "Construisons quelque chose ensemble.",
];

const PublicChatSection = ({ chatRef }: { chatRef: React.RefObject<HTMLDivElement> }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showSignup, setShowSignup] = useState(false);
  const [userMsgCount, setUserMsgCount] = useState(0);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const limitReached = userMsgCount >= MAX_FREE_EXCHANGES;
  const started = messages.length > 0;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Rotate placeholder every 4s while the chat hasn't started
  useEffect(() => {
    if (started) return;
    const id = setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % CHAT_PLACEHOLDERS.length);
    }, 4000);
    return () => clearInterval(id);
  }, [started]);

  const send = async (text: string) => {
    if (!text.trim() || loading || limitReached) return;
    const userMsg: ChatMsg = { role: "user", content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    const newCount = userMsgCount + 1;
    setUserMsgCount(newCount);

    try {
      const resp = await fetch(`${API_URL}/api/chat/public`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text.trim(), module: "orchestrator", session_id: sessionId }),
      });
      const data = await resp.json();
      if (!sessionId) setSessionId(data.session_id);

      let content = data.response || "";
      if (content.includes("SUGGEST_SIGNUP")) {
        content = content.replace(/\n*SUGGEST_SIGNUP\s*/g, "");
        setShowSignup(true);
      }
      setMessages((prev) => [...prev, { role: "assistant", content }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Bienvenue ! Parlez-moi de votre projet, je suis là pour vous aider." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  const quickPrompts = [
    "Je veux créer un site pour mon agence immobilière",
    "J'ai besoin d'un business plan pour ma start-up",
    "Comment booster mes ventes rapidement ?",
    "Je cherche à digitaliser mon restaurant",
  ];

  const hasMessages = messages.length > 0;

  return (
    <div ref={chatRef} className="scroll-mt-20">
      <div className="min-h-[70vh] md:h-[75vh] md:min-h-[550px] flex flex-col items-center relative overflow-hidden py-4 md:py-0">
        {/* Subtle background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,hsl(38_90%_55%_/_0.04),transparent)]" />
        </div>

        <div className="w-full max-w-4xl mx-auto px-4 flex flex-col h-full relative z-10">
          {/* Empty state — truly centered hero */}
          {!hasMessages && (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 min-h-0 animate-reveal-up">
              <div className="h-14 w-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Sparkles className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center">
                Comment puis-je t'aider ?
              </h2>
              <p className="text-muted-foreground text-base max-w-lg text-center">
                Décris ton projet, pose une question business, ou demande un conseil stratégique.
              </p>
            </div>
          )}

          {/* Conversation — scrollable area */}
          {hasMessages && (
            <div ref={scrollRef} className="flex-1 overflow-y-auto py-6 space-y-6 min-h-0">
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-4 max-w-3xl ${msg.role === "user" ? "ml-auto flex-row-reverse" : ""}`}>
                  {msg.role === "assistant" ? (
                    <div className="h-8 w-8 rounded-xl bg-primary/15 flex items-center justify-center shrink-0 mt-1">
                      <Sparkles className="h-4 w-4 text-primary" />
                    </div>
                  ) : (
                    <div className="h-8 w-8 rounded-xl bg-secondary flex items-center justify-center shrink-0 mt-1">
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className={`rounded-2xl px-5 py-3.5 text-sm leading-relaxed max-w-[85%] ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-card/80 border border-border/40"
                  }`}>
                    {msg.content.split("\n").map((line, j) => (
                      <p key={j} className={j > 0 ? "mt-2" : ""}>{line}</p>
                    ))}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-4">
                  <div className="h-8 w-8 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                    <Sparkles className="h-4 w-4 text-primary" />
                  </div>
                  <div className="bg-card/80 border border-border/40 rounded-2xl px-5 py-3.5">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              {/* Signup CTA */}
              {showSignup && (
                <div className="max-w-md mx-auto rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center space-y-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center mx-auto">
                    <UserPlus className="h-5 w-5 text-primary" />
                  </div>
                  <p className="text-sm font-semibold">Créez votre espace gratuit</p>
                  <p className="text-xs text-muted-foreground">Pour sécuriser votre projet et accéder à tous les outils.</p>
                  <Button variant="hero" size="sm" className="h-9 px-6" onClick={() => navigate("/auth")} data-testid="public-chat-signup-btn">
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Créer mon compte gratuit
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Input area — anchored at bottom when conversation starts */}
          <div className="shrink-0 pb-4 pt-2">
            {limitReached ? (
              <div className="text-center py-4 space-y-3">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold">
                  <Lock className="h-3.5 w-3.5" /> {MAX_FREE_EXCHANGES} consultations gratuites utilisées
                </div>
                <p className="text-sm text-muted-foreground">
                  Créez votre compte pour continuer — accès illimité au chat, templates et outils.
                </p>
                <Button variant="hero" className="h-12 px-8" onClick={() => navigate("/auth")} data-testid="credit-limit-signup-btn">
                  <UserPlus className="h-4 w-4 mr-2" /> Créer mon compte gratuit
                </Button>
              </div>
            ) : (
              <>
                <div className={`relative border rounded-2xl shadow-[0_8px_40px_rgba(200,150,50,0.15)] backdrop-blur-sm focus-within:border-primary transition-all duration-500 overflow-hidden ${
                  hasMessages
                    ? "bg-card/80 border-border/50"
                    : "bg-primary/5 border-primary/40 ring-1 ring-primary/10"
                }`}>
                  <textarea
                    ref={inputRef}
                    data-testid="public-chat-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={CHAT_PLACEHOLDERS[placeholderIdx]}
                    rows={3}
                    className="w-full resize-none bg-transparent px-4 pt-4 pb-2 md:px-5 md:pt-5 md:pb-14 text-sm placeholder:text-muted-foreground/50 focus:outline-none md:min-h-[120px] transition-[padding] duration-300"
                  />
                  {/* Controls row — static on mobile, absolute on desktop */}
                  <div className="flex items-center justify-end gap-2 px-3 pb-3 pt-1 md:absolute md:bottom-3 md:right-3 md:p-0 md:pt-0">
                    {userMsgCount > 0 && (
                      <div className="flex items-center gap-1.5 mr-1" data-testid="credit-counter">
                        <div className="flex gap-0.5">
                          {Array.from({ length: MAX_FREE_EXCHANGES }).map((_, i) => (
                            <div
                              key={i}
                              className={`h-1.5 w-4 rounded-full transition-colors ${
                                i < userMsgCount ? "bg-primary" : "bg-secondary/60"
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      data-testid="public-chat-send-btn"
                      className={`h-10 w-10 rounded-xl flex items-center justify-center transition-all shrink-0 ${
                        input.trim() && !loading
                          ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25"
                          : "bg-secondary/60 text-muted-foreground cursor-not-allowed"
                      }`}
                      onClick={() => send(input)}
                      disabled={!input.trim() || loading}
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowRight className="h-4 w-4 -rotate-90" />
                      )}
                    </button>
                  </div>
                </div>
                {!hasMessages && (
                  <>
                    <p className="text-[11px] text-muted-foreground/40 mt-3 text-center uppercase tracking-wider font-medium">
                      Vous ne savez pas par où commencer ? Essayez l'un de ces exemples :
                    </p>
                    <div className="flex flex-wrap justify-center gap-2 mt-2">
                      {quickPrompts.map((p) => (
                        <button
                          key={p}
                          onClick={() => send(p)}
                          className="text-xs px-4 py-2 rounded-full border border-border/40 bg-card/40 text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all"
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {hasMessages && (
                  <p className="text-[10px] text-muted-foreground/40 mt-2 text-center">
                    Grafton IA peut faire des erreurs. Vérifiez les informations importantes.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const problems = [
  "Vous passez des heures à analyser vos chiffres manuellement",
  "Vos décisions manquent de données fiables et en temps réel",
  "Votre trésorerie vous réserve des surprises en fin de mois",
  "Vous n'avez pas le temps de prospecter efficacement",
  "Créer un site web professionnel vous semble hors de portée",
];

const solutions = [
  { icon: Brain, title: "Analyse IA", desc: "Grafton décrypte vos KPIs en temps réel et identifie les leviers de croissance invisibles à l'oeil nu." },
  { icon: Target, title: "Plan d'action", desc: "Des recommandations concrètes et priorisées, générées automatiquement chaque semaine." },
  { icon: Landmark, title: "Pilotage financier", desc: "Surveillance 24/7 de votre trésorerie avec alertes prédictives et projections à 90 jours." },
  { icon: MessageSquare, title: "Copilote stratégique", desc: "Posez vos questions en langage naturel, obtenez des réponses stratégiques instantanées." },
  { icon: Globe, title: "Site web en 1 clic", desc: "9 templates professionnels prêts à l'emploi. Choisissez, personnalisez, publiez." },
  { icon: Zap, title: "Automatisation", desc: "Relances clients, rapports, factures — Grafton gère l'opérationnel pour vous." },
];

const showcaseTemplates = [
  { name: "Immobilier Luxe", category: "Immobilier", icon: Building2, color: "from-amber-900/30 to-amber-600/10", accent: "#C9A962", image: "/images/templates/immobilier-luxe.png" },
  { name: "Boutique", category: "E-Commerce", icon: ShoppingBag, color: "from-emerald-900/30 to-emerald-600/10", accent: "#10b981", image: "/images/templates/ecommerce-boutique.png" },
  { name: "Restaurant", category: "Food", icon: ChefHat, color: "from-orange-900/30 to-orange-600/10", accent: "#f97316", image: "/images/templates/restaurant-gastro.png" },
  { name: "Corporate Pro", category: "Business", icon: Briefcase, color: "from-primary/20 to-primary/5", accent: "hsl(38,90%,55%)", image: "/images/templates/corporate-pro.png" },
];

const testimonials = [
  {
    quote: "Grafton a identifié 3 leviers de croissance que nous n'avions jamais envisagés. Notre CA a progressé de 23% en 4 mois.",
    author: "Marie L.", role: "Dirigeante, Immobilier Premium", stars: 5,
  },
  {
    quote: "Le tunnel de vente configuré par Grafton IA convertit 3x plus que notre ancien processus. Un vrai game changer.",
    author: "Thomas D.", role: "Fondateur, SaaS B2B", stars: 5,
  },
  {
    quote: "La trésorerie est désormais pilotée en temps réel. Plus de mauvaises surprises en fin de mois.",
    author: "Sophie R.", role: "DAF, PME industrielle", stars: 5,
  },
  {
    quote: "En 48h, Grafton m'a livré un site professionnel ET un plan d'action commercial. Impressionnant.",
    author: "Karim B.", role: "Gérant, Restaurant gastronomique", stars: 5,
  },
];

const steps = [
  { num: "01", title: "Connectez-vous", desc: "Créez votre compte en 30 secondes. Grafton IA scanne immédiatement votre contexte.", icon: Rocket },
  { num: "02", title: "Recevez votre audit", desc: "En 48h, un rapport complet identifie vos forces, faiblesses et opportunités cachées.", icon: Search },
  { num: "03", title: "Passez à l'action", desc: "Des livrables concrets : site web, plan d'action, recommandations stratégiques.", icon: Zap },
];

/* ─── Page ─── */
const Index = () => {
  const { t } = useTranslation();
  const [activeProblem, setActiveProblem] = useState(0);
  const chatRef = useRef<HTMLDivElement>(null);

  const scrollToChat = () => {
    chatRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const interval = setInterval(() => setActiveProblem((p) => (p + 1) % problems.length), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <PublicLayout>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,hsl(38_90%_55%_/_0.08),transparent)]" />
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-primary/[0.04] rounded-full blur-[120px] animate-pulse" style={{ animationDuration: "6s" }} />
          <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-primary/[0.03] rounded-full blur-[80px] animate-pulse" style={{ animationDuration: "8s" }} />
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: "linear-gradient(hsl(38 90% 55% / 0.3) 1px, transparent 1px), linear-gradient(90deg, hsl(38 90% 55% / 0.3) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }} />
        </div>

        <div className="container relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — Copy */}
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-8 animate-reveal-up" style={{ animationDelay: "0ms" }}>
                <Sparkles className="h-3.5 w-3.5" />
                {t("landing.heroBadge")}
              </div>

              <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6 animate-reveal-up" style={{ animationDelay: "80ms" }}>
                {t("landing.heroTitle1")}<br />
                <TypingText words={[t("landing.heroTitle2"), t("landing.heroTitle3")]} />
              </h1>

              <p className="text-lg text-muted-foreground leading-relaxed mb-8 animate-reveal-up" style={{ animationDelay: "160ms" }}>
                {t("landing.heroSubtitle")}
              </p>

              <div className="flex flex-wrap gap-3 mb-8 animate-reveal-up" style={{ animationDelay: "240ms" }}>
                <Button data-testid="hero-cta-btn" variant="hero" size="lg" className="h-14 px-8 text-base shadow-[0_4px_24px_hsl(38_90%_55%_/_0.25)] hover:shadow-[0_8px_40px_hsl(38_90%_55%_/_0.35)] transition-all" onClick={scrollToChat}>
                  {t("landing.heroCTA")} <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" size="lg" className="h-14 px-8 text-base border-border/50 hover:border-primary/30" onClick={scrollToChat}>
                  <Play className="mr-2 h-4 w-4" /> {t("landing.heroCTAsecond")}
                </Button>
              </div>

              <div className="flex items-center gap-6 text-sm text-muted-foreground animate-reveal-up" style={{ animationDelay: "320ms" }}>
                <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" />Gratuit 14 jours</span>
                <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" />Sans carte bancaire</span>
                <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-primary" />Setup en 2 min</span>
              </div>
            </div>

            {/* Right — Dashboard mockup */}
            <div className="hidden lg:block animate-reveal-up" style={{ animationDelay: "400ms" }}>
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ PUBLIC CHAT ═══════════════════════ */}
      <PublicChatSection chatRef={chatRef} />

      {/* ═══════════════════════ SOCIAL PROOF BAR ═══════════════════════ */}
      <section className="border-y border-border/30 bg-card/20 backdrop-blur-sm">
        <div className="container py-10">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.25em] text-muted-foreground mb-8">
            Ils font confiance à Grafton IA
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 200, suffix: "+", label: "Entreprises pilotées" },
              { value: 98, suffix: "%", label: "Taux de satisfaction" },
              { value: 35, prefix: "+", suffix: "%", label: "Croissance moyenne" },
              { value: 48, suffix: "h", label: "Premier livrable" },
            ].map((stat) => (
              <ScrollReveal key={stat.label}>
                <p className="text-3xl md:text-4xl font-bold text-primary"><Counter end={stat.value} suffix={stat.suffix} prefix={stat.prefix || ""} /></p>
                <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ PROBLEM → SOLUTION ═══════════════════════ */}
      <section className="py-20 md:py-24">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left — Problem */}
            <ScrollReveal>
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-red-400 mb-4 block">Le problème</span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
                  Vous reconnaissez-vous ?
                </h2>
                <div className="space-y-3">
                  {problems.map((p, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 p-4 rounded-xl border transition-all duration-500 cursor-pointer ${
                        i === activeProblem
                          ? "border-red-500/30 bg-red-500/5 shadow-[0_0_24px_rgba(239,68,68,0.08)]"
                          : "border-border/30 bg-transparent hover:border-border/60"
                      }`}
                      onClick={() => setActiveProblem(i)}
                    >
                      <div className={`h-2 w-2 rounded-full shrink-0 transition-colors ${i === activeProblem ? "bg-red-400" : "bg-muted-foreground/30"}`} />
                      <p className={`text-sm transition-colors ${i === activeProblem ? "text-foreground" : "text-muted-foreground"}`}>{p}</p>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            {/* Right — Solution */}
            <ScrollReveal delay={200}>
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">La solution</span>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">
                  Grafton fait le travail <span className="text-gradient">pour vous</span>
                </h2>
                <div className="space-y-4">
                  {solutions.slice(0, 4).map((s, i) => (
                    <div key={s.title} className="flex items-start gap-4 group">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
                        <s.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm mb-0.5">{s.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════ LIVE DEMO SHOWCASE ═══════════════════════ */}
      <section className="py-20 md:py-24 border-t border-border/30 bg-card/10">
        <div className="container">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">Résultat immédiat</span>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                Un livrable concret <span className="text-gradient">dès le premier jour</span>
              </h2>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                Pas de promesses vides. Grafton vous livre un site web professionnel,
                un plan d'action ou un audit complet — immédiatement.
              </p>
            </div>
          </ScrollReveal>

          {/* Template showcase */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {showcaseTemplates.map((t, i) => (
              <ScrollReveal key={t.name} delay={i * 100}>
                <Link to="/auth" className="group block">
                  <div className="aspect-[4/3] rounded-xl border border-border/30 relative overflow-hidden group-hover:border-primary/30 transition-all duration-300 group-hover:shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                    <img
                      src={t.image}
                      alt={t.name}
                      className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 backdrop-blur-[2px]">
                      <div className="flex items-center gap-2 text-white text-sm font-medium bg-primary/80 px-4 py-2 rounded-lg">
                        <Eye className="h-4 w-4" /> Prévisualiser
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-white/70">{t.category}</span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <h3 className="text-sm font-semibold group-hover:text-primary transition-colors">{t.name}</h3>
                    <p className="text-xs text-muted-foreground">{t.category}</p>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={500}>
            <div className="text-center mt-12">
              <Link to="/auth">
                <Button variant="hero" size="lg" className="h-12 px-8">
                  Voir les 9 templates <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══════════════════════ CAPABILITIES GRID ═══════════════════════ */}
      <section className="py-20 md:py-24 border-t border-border/30">
        <div className="container">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">Fonctionnalités</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Tout ce dont votre entreprise a besoin</h2>
              <p className="text-muted-foreground text-lg max-w-xl mx-auto">Une plateforme complète qui centralise l'intelligence de votre entreprise.</p>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {solutions.map((s, i) => (
              <ScrollReveal key={s.title} delay={i * 80}>
                <div className="group rounded-xl border border-border/40 bg-card/30 p-7 hover:border-primary/25 hover:bg-card/50 transition-all duration-300 hover:shadow-[0_8px_32px_hsl(var(--glow-amber)/0.06)]">
                  <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/15 transition-colors">
                    <s.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-base mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ METHOD (3 Steps) ═══════════════════════ */}
      <section className="py-20 md:py-24 border-t border-border/30 bg-card/10">
        <div className="container">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">Comment ça marche</span>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight">3 étapes. <span className="text-gradient">48 heures.</span></h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <ScrollReveal key={step.num} delay={i * 150}>
                <div className="text-center relative">
                  {/* Connector line */}
                  {i < steps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-primary/30 to-transparent" />
                  )}
                  <div className="h-20 w-20 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6 relative">
                    <step.icon className="h-8 w-8 text-primary" />
                    <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center">
                      {step.num}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ TESTIMONIALS ═══════════════════════ */}
      <section className="py-20 md:py-24 border-t border-border/30">
        <div className="container">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">Témoignages</span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Ils pilotent leur entreprise avec Grafton</h2>
            </div>
          </ScrollReveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t, i) => (
              <ScrollReveal key={i} delay={i * 80}>
                <div className="rounded-xl border border-border/40 bg-card/30 p-6 hover:border-primary/20 transition-all h-full flex flex-col">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: t.stars }).map((_, j) => (
                      <Star key={j} className="h-3.5 w-3.5 text-primary fill-primary" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1 italic">"{t.quote}"</p>
                  <div className="mt-5 pt-4 border-t border-border/30">
                    <p className="text-sm font-semibold">{t.author}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════ FINAL CTA ═══════════════════════ */}
      <section className="py-20 md:py-24 border-t border-border/30">
        <div className="container">
          <ScrollReveal>
            <div className="relative rounded-2xl overflow-hidden">
              {/* Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-card to-primary/5" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(38_90%_55%_/_0.12),transparent)]" />
              <div className="absolute inset-0 border border-primary/15 rounded-2xl" />

              <div className="relative px-8 py-16 md:py-24 text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-8">
                  <Sparkles className="h-3.5 w-3.5" /> Prêt à passer à l'action ?
                </div>

                <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-2xl mx-auto leading-[1.1]">
                  Votre premier livrable <span className="text-gradient">en 48 heures</span>
                </h2>

                <p className="text-muted-foreground text-lg max-w-lg mx-auto mb-10 leading-relaxed">
                  Site web professionnel, plan d'action commercial ou audit stratégique —
                  Grafton IA vous livre du concret, pas des promesses.
                </p>

                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Button data-testid="cta-btn" variant="hero" size="lg" className="h-14 px-10 text-base shadow-[0_4px_24px_hsl(38_90%_55%_/_0.25)] hover:shadow-[0_8px_40px_hsl(38_90%_55%_/_0.35)] transition-all" onClick={scrollToChat}>
                    Commencer gratuitement <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground mt-6">
                  Gratuit 14 jours · Sans carte bancaire · Annulation libre
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      <LandingChat />
    </PublicLayout>
  );
};

export default Index;
